'use client';

import { forwardRef, useState, useRef, useEffect, useLayoutEffect, ReactNode, HTMLAttributes, CSSProperties, Ref } from 'react';
import { createPortal } from 'react-dom';
import { usePortalContainer } from '../primitives/portal-container';
import Button from '../actions/Button';
import styles from './Tooltip.module.css';

type TooltipPosition = 'top' | 'right' | 'bottom' | 'left';
type TooltipVariant = 'plain' | 'rich';

interface TooltipProps extends HTMLAttributes<HTMLElement> {
  content: string;
  title?: string; // for rich variant
  action?: { label: string; onClick: () => void }; // for rich variant
  position?: TooltipPosition;
  variant?: TooltipVariant;
  delay?: number;
  /**
   * Render the popup in a portal (position: fixed) so it escapes ancestor
   * overflow/scroll clipping and is valid inside interactive elements like a
   * <button>. In portal mode the wrapper is a <span> (phrasing content).
   */
  portal?: boolean;
  children: ReactNode;
  /**
   * Render the portal into this element instead of `document.body`.
   * Falls back to `PortalContainerContext`, then `document.body`.
   */
  portalContainer?: Element | null;
}

interface Rect { top: number; left: number; right: number; bottom: number; width: number; height: number; }
interface Size { w: number; h: number; }

const GAP = 8;
/** Keep a clamped popup this far off the window edge. */
const EDGE = 8;

// Top-left corner coordinates for the portalled popup, in fixed-position pixels.
// The popup is positioned by its own corner — never by a translate — so `transform`
// is left entirely to the scale-in entrance (same as a non-portal tooltip) and the
// position never animates. Until the popup has been measured there's no corner to
// pin, so it's parked hidden; the layout effect measures it and re-places it before
// the browser paints, and only then is it shown. Coordinates are clamped inside the
// viewport, since a trigger in a right-hand panel would otherwise push it off-screen.
function portalCoords(position: TooltipPosition, r: Rect, size: Size | null): CSSProperties {
  const cx = r.left + r.width / 2;
  const cy = r.top + r.height / 2;
  const base: CSSProperties = { position: 'fixed', bottom: 'auto', right: 'auto' };

  // Measuring pass: in the DOM (so it has a real box) but not yet shown.
  if (!size) return { ...base, left: 0, top: 0, visibility: 'hidden' };

  const vw = typeof window === 'undefined' ? 0 : window.innerWidth;
  const vh = typeof window === 'undefined' ? 0 : window.innerHeight;
  const x = (left: number) => Math.max(EDGE, Math.min(left, vw - size.w - EDGE));
  const y = (top: number) => Math.max(EDGE, Math.min(top, vh - size.h - EDGE));

  switch (position) {
    case 'top':    return { ...base, left: x(cx - size.w / 2), top: y(r.top - GAP - size.h) };
    case 'bottom': return { ...base, left: x(cx - size.w / 2), top: y(r.bottom + GAP) };
    case 'right':  return { ...base, left: x(r.right + GAP),   top: y(cy - size.h / 2) };
    case 'left':   return { ...base, left: x(r.left - GAP - size.w), top: y(cy - size.h / 2) };
  }
}

const Tooltip = forwardRef<HTMLElement, TooltipProps>(({
  content,
  title,
  action,
  position = 'top',
  variant = 'plain',
  delay = 300,
  portal = false,
  portalContainer,
  children,
  className = '',
  ...props
}, ref) => {
  const portalTarget = usePortalContainer(portalContainer);
  const [visible, setVisible] = useState(false);
  const [rect, setRect] = useState<Rect | null>(null);
  const [size, setSize] = useState<Size | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRef = useRef<HTMLElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const showTooltip = () => {
    timeoutRef.current = setTimeout(() => {
      if (portal && triggerRef.current) {
        const r = triggerRef.current.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, right: r.right, bottom: r.bottom, width: r.width, height: r.height });
      }
      setVisible(true);
    }, delay);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setVisible(false);
    // Measured afresh on the next show: the content, or the window, may have changed.
    setSize(null);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // Measure the popup the instant it mounts, then re-place it against its real box.
  // useLayoutEffect, not useEffect: it fires before the browser paints, so the
  // first-pass (centre-on-trigger) position is never shown — otherwise the popup
  // is seen flying from there to its clamped spot.
  useLayoutEffect(() => {
    if (!portal || !visible || size || !popupRef.current) return;
    const { offsetWidth: w, offsetHeight: h } = popupRef.current;
    if (w > 0 && h > 0) setSize({ w, h });
  }, [portal, visible, size]);

  // A portalled popup is positioned once on show; scrolling would detach it from the
  // trigger, so dismiss it instead.
  useEffect(() => {
    if (!portal || !visible) return;
    const dismiss = () => hideTooltip();
    window.addEventListener('scroll', dismiss, true);
    return () => window.removeEventListener('scroll', dismiss, true);
  }, [portal, visible]);

  const resolvedVariant = title || action ? 'rich' : variant;
  const isMultiline = content.length > 32 || resolvedVariant === 'rich';

  // A portalled popup only counts as shown once it's been measured and placed —
  // until then it's parked off-screen and must not carry the visible (opacity/scale)
  // state, or the entrance would play at the wrong spot.
  const placed = !portal || !!size;
  const tooltipClassNames = [
    styles.tooltip,
    styles[position],
    styles[resolvedVariant],
    portal ? styles.portal : '',
    isMultiline ? styles.multiline : '',
    visible && placed ? styles.visible : '',
  ].filter(Boolean).join(' ');

  const wrapperClassNames = [styles.wrapper, className].filter(Boolean).join(' ');

  const handlers = {
    onMouseEnter: showTooltip,
    onMouseLeave: hideTooltip,
    onFocus: showTooltip,
    onBlur: hideTooltip,
  };

  const popup = (
    <div
      ref={popupRef}
      className={tooltipClassNames}
      role="tooltip"
      aria-hidden={!visible}
      style={portal && rect ? portalCoords(position, rect, size) : undefined}
    >
      <div className={styles.arrow} />
      {title && <div className={styles.title}>{title}</div>}
      <div className={styles.content}>{content}</div>
      {action && (
        <Button
          variant="text"
          size="sm"
          className={styles.action}
          onClick={(e) => {
            e.stopPropagation();
            action.onClick();
            hideTooltip();
          }}
          type="button"
        >
          {action.label}
        </Button>
      )}
    </div>
  );

  // Portal mode: <span> wrapper/trigger (valid inside a button) + popup rendered to
  // document.body so scroll/overflow can't clip it.
  if (portal) {
    return (
      <span ref={ref as Ref<HTMLSpanElement>} className={wrapperClassNames} {...handlers} {...props}>
        <span ref={triggerRef as Ref<HTMLSpanElement>} className={styles.trigger}>
          {children}
        </span>
        {visible && portalTarget ? createPortal(popup, portalTarget) : null}
      </span>
    );
  }

  return (
    <div ref={ref as Ref<HTMLDivElement>} className={wrapperClassNames} {...handlers} {...props}>
      <div ref={triggerRef as Ref<HTMLDivElement>} className={styles.trigger}>
        {children}
      </div>
      {popup}
    </div>
  );
});

Tooltip.displayName = 'Tooltip';
export default Tooltip;
